import { useContext, useEffect, useMemo, useState } from 'react';
import { Table, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { FaPlus, FaEdit, FaTrash, FaEraser } from 'react-icons/fa';
import axios from 'axios';

import DeleteModal from '../../components/modals/DeleteModal';
import SuccessModal from '../../components/modals/SuccessModal';
import { useNotificationModal } from '../../hooks/useNotificationModal';
import { formatPhoneWithCountryCode } from '../../utils/phoneFormatter';

import '../../styles/ViewAdmins.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Mapping for responsibility labels
const responsibilityLabels = {
  category: 'Create and manage product categories',
  style: 'Create and manage style with attributes',
  merchandising_po: 'Create and manage purchase orders',
  warehouse_grn: 'Goods Receipt Note - Record incoming inventory',
  warehouse_issuing: 'Issue inventory for orders',
  warehouse_stock: 'Monitor inventory levels',
  warehouse_offers: 'Create and manage product discount offers',
  warehouse_stock_allocation: 'Manage Stock allocations',
  finance_currency: 'Manage company currencies and exchange rates',
  finance_supplier: 'Register and manage supplier information',
  finance_location: 'Manage warehouse and business locations',
  accounting_tax: 'Configure tax rates and tax calculations',
  approve_po: 'Review and approve purchase orders',
  manage_plants: 'Manage plants and plant managers',
  manage_branches: 'Manage branches and branch managers',
  manage_warehouse_locations: 'Manage warehouse locations',
};

export default function ViewAdmins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userData } = useContext(AuthContext);
  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showError,
    hideSuccess,
    hideError
  } = useNotificationModal();

  const [deleteModalInfo, setDeleteModalInfo] = useState({ id: null, confirmMessage: '' });
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [columnFilter, setColumnFilter] = useState({
    Name: '',
    Email: '',
    Phone: '',
    Role: '',
    Responsibilities: ''
  });

  const navigate = useNavigate();
  const company_code = userData?.company_code;

  // clear filter
  const clearFilter = () => {
    setColumnFilter({
      Name: '',
      Email: '',
      Phone: '',
      Role: '',
      Responsibilities: ''
    });
  }

  // handle filter change
  const handleFilterChange = (e, column) => {
    const value = e.target.value;
    setColumnFilter((prevFilters) => ({
      ...prevFilters,
      [column]: value,
    }));
  }

  // Helper function to format responsibilities
  const formatResponsibilities = (sideBarOptions, role) => {
    if (role === "Company_Admin") {
      return "All responsibilities assigned";
    }

    if (
      !sideBarOptions ||
      !Array.isArray(sideBarOptions) ||
      sideBarOptions.length === 0
    ) {
      return "No responsibilities assigned";
    }


    const formattedResponsibilities = sideBarOptions
      .map(option => responsibilityLabels[option] || option)
      .join(', ');

    return formattedResponsibilities;
  };

  // Handler to open delete modal with custom message
  const handleOpenDeleteModal = (admin) => {
    const confirmMessage = `Are you sure you want to delete admin "${admin.Name}"? This action cannot be undone.`;
    setDeleteModalInfo({ id: admin.user_id, confirmMessage });
  };


  // Fetch admins from server
  const fetchAdmins = async () => {
    if (!company_code) {
      showError('Company code not available', 'Error');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/api/admin/auth/get-all-admins`, {
        params: { company_code }
      });
      if (response.status === 200 && response.data.success) {
        setAdmins(response.data.admins);
        setFilteredAdmins(response.data.admins);
      } else {
        showError(response.data.message || 'Failed to fetch admin list', 'Error');
      }
    } catch (error) {
      showError(error.message || 'Server error. Please try again later.', 'Error');
      console.error('Error fetching admin list:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handler for delete success: close modal first, then refresh
  const handleDeleteSuccess = () => {
    setDeleteModalInfo({ id: null, confirmMessage: '' });
    fetchAdmins();
  };

  useEffect(() => {
    fetchAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company_code]);

  // Apply column filters
  useMemo(() => {
    let filtered = admins;

    // Filter by Name
    if (columnFilter.Name) {
      filtered = filtered.filter((admin) =>
        admin.Name.toLowerCase().includes(columnFilter.Name.toLowerCase())
      );
    }

    // Filter by Email
    if (columnFilter.Email) {
      filtered = filtered.filter((admin) =>
        admin.Email.toLowerCase().includes(columnFilter.Email.toLowerCase())
      );
    }

    // Filter by Phone
    if (columnFilter.Phone) {
      filtered = filtered.filter((admin) => {
        const formattedPhone = formatPhoneWithCountryCode(
          admin.Country_Code,
          admin.Phone
        );
        return formattedPhone.toLowerCase().includes(columnFilter.Phone.toLowerCase());
      });
    }

    // Filter by Role
    if (columnFilter.Role) {
      filtered = filtered.filter((admin) => {
        return admin.Role.toLowerCase().includes(columnFilter.Role.toLowerCase());
      });
    }

    // Filter by Responsibilities
    if (columnFilter.Responsibilities) {
      filtered = filtered.filter((admin) => {
        const responsibilities = formatResponsibilities(
          admin.side_bar_options,
          admin.Role
        );
        return responsibilities.toLowerCase().includes(columnFilter.Responsibilities.toLowerCase());
      });
    }

    setFilteredAdmins(filtered);
  }, [admins, columnFilter]);

  return (
    <div className="admin-container mt-4">
      <div className="style-header">
        <h1>Company Users</h1>
        <div>
          <button
            className="btn-custom-primary"
            onClick={() => navigate("/register")}
          >
            <FaPlus size={14} style={{ marginRight: "8px" }} />
            Add User
          </button>
        </div>
      </div>

      {!loading && (
        <div className="table-responsive">
          <Table className="align-middle company-admin-table" hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Responsibilities</th>
                <th>Actions</th>
              </tr>
              <tr>
                <th></th>
                <th>
                  <Form.Control
                    className='table-column-filter'
                    type="text"
                    placeholder="Search name"
                    value={columnFilter.Name}
                    onChange={(e) => handleFilterChange(e, "Name")}
                  />
                </th>
                <th>
                  <Form.Control
                    className='table-column-filter'
                    type="text"
                    placeholder="Search email"
                    value={columnFilter.Email}
                    onChange={(e) => handleFilterChange(e, "Email")}
                  />
                </th>
                <th>
                  <Form.Control
                    className='table-column-filter'
                    type="text"
                    placeholder="Search phone"
                    value={columnFilter.Phone}
                    onChange={(e) => handleFilterChange(e, "Phone")}
                  />
                </th>
                <th>
                  <Form.Control
                    className="table-column-filter"
                    type="text"
                    placeholder="Search role"
                    value={columnFilter.Role}
                    onChange={(e) => handleFilterChange(e, "Role")}
                  >
                  </Form.Control>
                </th>
                <th>
                  <Form.Control
                    className='table-column-filter'
                    type="text"
                    placeholder="Search responsibilities"
                    value={columnFilter.Responsibilities}
                    onChange={(e) => handleFilterChange(e, "Responsibilities")}
                  />
                </th>
                <th>
                  <FaEraser
                    className="pf-clear-filters-btn"
                    onClick={clearFilter}
                    title="Clear All Filters"
                    size={13}
                    style={{ cursor: "pointer" }}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAdmins.map((admin_users, index) => (
                <tr key={admin_users.id || index}>
                  <td>{index + 1}</td>
                  <td>{admin_users.Name}</td>
                  <td>{admin_users.Email}</td>
                  <td>
                    {formatPhoneWithCountryCode(
                      admin_users.Country_Code,
                      admin_users.Phone
                    )}
                  </td>
                  <td>{admin_users.Role}</td>
                  <td
                    title={formatResponsibilities(
                      admin_users.side_bar_options,
                      admin_users.Role
                    )}
                  >
                    {formatResponsibilities(
                      admin_users.side_bar_options,
                      admin_users.Role
                    )}
                  </td>
                  <td>
                    <FaEdit
                      className="action-icon me-2"
                      onClick={() =>
                        navigate(`/EditAdmins/${admin_users.user_id}`)
                      }
                      title="Edit"
                      style={{ cursor: "pointer", color: "#007bff" }}
                    />
                    <FaTrash
                      className="action-icon text-danger"
                      onClick={() => handleOpenDeleteModal(admin_users)}
                      title="Delete"
                      style={{ cursor: "pointer" }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      {/* Delete Modal (single instance, controlled by state) */}
      <DeleteModal
        id={deleteModalInfo.id}
        show={!!deleteModalInfo.id}
        onHide={() => setDeleteModalInfo({ id: null, confirmMessage: "" })}
        // Pass a function to build the delete URL for this entity
        deleteUrl={(id) =>
          id
            ? `/api/admin/auth/delete-admin/${id}?company_code=${company_code}`
            : ""
        }
        entityLabel="admin"
        confirmMessage={deleteModalInfo.confirmMessage}
        onDeleteSuccess={handleDeleteSuccess}
      />

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        message={successMessage}
        title={successTitle}
        variant="success"
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        message={errorMessage}
        title={errorTitle}
        variant="error"
      />
    </div>
  );
}
